import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import TodoAttachment, { TodoFileRef } from "./TodoAttachment";
import {
    REMIND_OPTIONS,
    REPEAT_OPTIONS,
    STATUSES,
    buildTaskText,
    copyToClipboard,
    errorText,
    fromInputValue,
    getId,
    isOverdue,
    toInputValue,
    userName,
    whenText,
} from "./todoUtils";

/*
 * Задача целиком: название, статус, исполнитель, срок с напоминанием и
 * повтором, описание, подзадачи, файлы и голосовые, комментарии.
 * Каждая правка сразу уходит на сервер; что не сохранилось — пишем здесь же.
 */
export default function TodoTaskPanel({
    task,
    subtasks = [],
    list,
    meId = "",
    actions,
    onClose,
    onShare,
}) {
    const [title, setTitle] = useState(task.title || "");
    const [description, setDescription] = useState(task.description || "");
    const [due, setDue] = useState(toInputValue(task.dueAt));
    const [subtaskTitle, setSubtaskTitle] = useState("");
    const [comment, setComment] = useState("");
    /** Файл, к которому пишется комментарий. Пусто — комментарий ко всей задаче. */
    const [commentFileId, setCommentFileId] = useState("");
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [saving, setSaving] = useState(false);
    const [uploads, setUploads] = useState([]);
    const [dragging, setDragging] = useState(false);

    const editingRef = useRef({ title: false, description: false, due: false });
    const fileInputRef = useRef(null);
    const commentRef = useRef(null);
    const titleRef = useRef(null);
    const aliveRef = useRef(true);

    useEffect(() => {
        aliveRef.current = true;
        return () => {
            aliveRef.current = false;
        };
    }, []);

    // Задачу могли изменить с телефона или другой вкладки: поля, которые сейчас
    // не редактируют, показывают свежие значения.
    useEffect(() => {
        if (!editingRef.current.title) setTitle(task.title || "");
    }, [task._id, task.title]);

    useEffect(() => {
        if (!editingRef.current.description) setDescription(task.description || "");
    }, [task._id, task.description]);

    useEffect(() => {
        if (!editingRef.current.due) setDue(toInputValue(task.dueAt));
    }, [task._id, task.dueAt]);

    // Поле названия растёт вместе с текстом: длинное название видно целиком.
    useEffect(() => {
        const node = titleRef.current;
        if (!node) return;
        node.style.height = "auto";
        node.style.height = `${node.scrollHeight}px`;
    }, [title]);

    // Открыли другую задачу — черновики прежней сюда не переносим.
    useEffect(() => {
        setSubtaskTitle("");
        setComment("");
        setCommentFileId("");
        setError("");
        setNotice("");
        setUploads([]);
    }, [task._id]);

    useEffect(() => {
        if (!notice) return undefined;
        const timer = window.setTimeout(() => setNotice(""), 2200);
        return () => window.clearTimeout(timer);
    }, [notice]);

    /** Выполнить действие и показать ошибку, если сервер отказал. */
    const run = useCallback(async (job, fallback) => {
        setError("");
        setSaving(true);

        try {
            return await job();
        } catch (jobError) {
            if (aliveRef.current) setError(errorText(jobError, fallback));
            return null;
        } finally {
            if (aliveRef.current) setSaving(false);
        }
    }, []);

    const patch = useCallback(
        (changes) => run(() => actions.updateTask(task._id, changes), "Не удалось сохранить"),
        [actions, run, task._id]
    );

    const members = Array.isArray(list?.members) ? list.members : [];
    const ownerId = String(list?.ownerId || "");
    const isOwner = !!meId && ownerId === String(meId);
    const canDeleteTask = isOwner || getId(task.createdBy) === String(meId);
    const overdue = isOverdue(task);
    const repeat = task.repeat?.every || "none";
    const remind = Number.isFinite(task.remindBeforeMin) ? task.remindBeforeMin : -1;
    const attachments = Array.isArray(task.attachments) ? task.attachments : [];

    const comments = useMemo(
        () => [...(task.comments || [])].sort(
            (a, b) => String(a.createdAt || "").localeCompare(String(b.createdAt || ""))
        ),
        [task.comments]
    );

    const commentCounts = useMemo(() => {
        const counts = {};
        comments.forEach((item) => {
            if (item.attachmentId) counts[item.attachmentId] = (counts[item.attachmentId] || 0) + 1;
        });
        return counts;
    }, [comments]);

    const fileById = useMemo(() => {
        const map = {};
        attachments.forEach((item) => {
            map[item._id] = item;
        });
        return map;
    }, [attachments]);

    const commentFile = commentFileId ? fileById[commentFileId] : null;

    // ---------- название, описание, срок ----------

    const saveTitle = () => {
        editingRef.current.title = false;
        const clean = title.trim();

        if (!clean) {
            setTitle(task.title || "");
            return;
        }
        if (clean !== String(task.title || "")) patch({ title: clean });
    };

    const saveDescription = () => {
        editingRef.current.description = false;
        const clean = description.trim();
        if (clean !== String(task.description || "").trim()) patch({ description: clean });
    };

    const saveDue = () => {
        editingRef.current.due = false;
        const next = fromInputValue(due);
        const current = task.dueAt ? new Date(task.dueAt).toISOString() : null;

        // Поле стёрли — срок убираем; дата не дописана — возвращаем прежнюю.
        if (!due) {
            if (current) patch({ dueAt: null });
            return;
        }
        if (!next) {
            setDue(toInputValue(task.dueAt));
            return;
        }
        if (next !== current) patch({ dueAt: next });
    };

    // ---------- подзадачи ----------

    const addSubtask = async () => {
        const clean = subtaskTitle.trim();
        if (!clean) return;

        const created = await run(
            () => actions.createTask(task.listId, { title: clean, parentId: task._id }),
            "Не удалось добавить подзадачу"
        );
        if (created && aliveRef.current) setSubtaskTitle("");
    };

    // ---------- файлы ----------

    const uploadFiles = async (fileList) => {
        const files = Array.from(fileList || []).filter(Boolean);
        if (!files.length) return;

        setError("");

        // По очереди: порядок файлов в задаче — как их выбрали.
        for (const file of files) {
            const key = `${Date.now()}-${Math.random()}`;
            setUploads((current) => [...current, { key, name: file.name, percent: 0 }]);

            try {
                await actions.addAttachment(task._id, file, (percent) => {
                    if (!aliveRef.current) return;
                    setUploads((current) => current.map((item) => (item.key === key ? { ...item, percent } : item)));
                });
            } catch (uploadError) {
                if (aliveRef.current) setError(`«${file.name}»: ${errorText(uploadError, "не удалось отправить файл")}`);
            } finally {
                if (aliveRef.current) setUploads((current) => current.filter((item) => item.key !== key));
            }
        }
    };

    const startFileComment = (attachment) => {
        setCommentFileId((current) => (current === attachment._id ? "" : attachment._id));
        commentRef.current?.focus();
    };

    // ---------- комментарии ----------

    const sendComment = async () => {
        const clean = comment.trim();
        if (!clean) return;

        const saved = await run(
            () => actions.addComment(task._id, clean, commentFile ? commentFile._id : ""),
            "Не удалось отправить комментарий"
        );
        if (saved && aliveRef.current) {
            setComment("");
            setCommentFileId("");
        }
    };

    const copyAsText = async () => {
        const text = buildTaskText(task, subtasks, (user) => userName(user, "", ""));
        setNotice((await copyToClipboard(text)) ? "Задача скопирована текстом" : "Не удалось скопировать");
    };

    const removeTask = async () => {
        const extra = subtasks.length ? ` Вместе с ней удалятся подзадачи (${subtasks.length}).` : "";
        if (!window.confirm(`Удалить задачу «${task.title}»?${extra}`)) return;
        await run(() => actions.deleteTask(task), "Не удалось удалить задачу");
    };

    const doneSubtasks = subtasks.filter((item) => item.status === "done").length;

    return (
        <aside className="todoPanel" aria-label="Задача">
            <div className="todoPanel__head">
                <span className="todoPanel__list" title={list?.title || ""}>{list?.title || "Список"}</span>
                <span className="todoPanel__headActions">
                    {saving ? <i className="todoSpinner" aria-label="Сохраняем" /> : null}
                    <button type="button" onClick={onClose} title="Закрыть задачу" aria-label="Закрыть задачу">✕</button>
                </span>
            </div>

            <div className="todoPanel__scroll">
                <textarea
                    ref={titleRef}
                    className="todoPanel__title"
                    value={title}
                    rows={1}
                    maxLength={300}
                    placeholder="Название задачи"
                    onFocus={() => { editingRef.current.title = true; }}
                    onChange={(event) => setTitle(event.target.value.replace(/\n/g, " "))}
                    onBlur={saveTitle}
                    onKeyDown={(event) => {
                        if (event.key === "Enter") {
                            event.preventDefault();
                            event.currentTarget.blur();
                        }
                    }}
                />

                {task.titleFromVoice ? (
                    <div className="todoPanel__hint">Название появится само, когда голосовое расшифруется.</div>
                ) : null}

                <div className="todoPanel__statuses" role="group" aria-label="Статус">
                    {STATUSES.map((item) => (
                        <button
                            key={item.value}
                            type="button"
                            className={`is-${item.value} ${task.status === item.value ? "is-active" : ""}`}
                            onClick={() => task.status !== item.value && patch({ status: item.value })}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>

                <div className="todoPanel__flags">
                    <label>
                        <input
                            type="checkbox"
                            checked={task.priority === "high"}
                            onChange={(event) => patch({ priority: event.target.checked ? "high" : "normal" })}
                        />
                        Важная
                    </label>
                    <label>
                        <input
                            type="checkbox"
                            checked={!!task.pinned}
                            onChange={(event) => patch({ pinned: event.target.checked })}
                        />
                        Закрепить наверху списка
                    </label>
                </div>

                <div className="todoPanel__grid">
                    <label className="todoField">
                        <span>Исполнитель</span>
                        <select
                            value={getId(task.assignee)}
                            onChange={(event) => patch({ assigneeId: event.target.value })}
                        >
                            <option value="">Без исполнителя</option>
                            {members.map((member) => (
                                <option key={getId(member)} value={getId(member)}>
                                    {userName(member, meId)}
                                </option>
                            ))}
                        </select>
                    </label>

                    <label className={`todoField ${overdue ? "is-overdue" : ""}`}>
                        <span>Срок{overdue ? " · просрочено" : ""}</span>
                        <span className="todoField__row">
                            <input
                                type="datetime-local"
                                value={due}
                                onFocus={() => { editingRef.current.due = true; }}
                                onChange={(event) => setDue(event.target.value)}
                                onBlur={saveDue}
                                onKeyDown={(event) => {
                                    if (event.key === "Enter") event.currentTarget.blur();
                                }}
                            />
                            {task.dueAt ? (
                                <button type="button" onClick={() => patch({ dueAt: null })} title="Убрать срок">✕</button>
                            ) : null}
                        </span>
                    </label>

                    <label className="todoField">
                        <span>Напомнить</span>
                        <select
                            value={remind}
                            disabled={!task.dueAt}
                            onChange={(event) => patch({ remindBeforeMin: Number(event.target.value) })}
                        >
                            {REMIND_OPTIONS.map((item) => (
                                <option key={item.value} value={item.value}>{item.label}</option>
                            ))}
                            {REMIND_OPTIONS.some((item) => item.value === remind) ? null : (
                                <option value={remind}>За {remind} мин</option>
                            )}
                        </select>
                    </label>

                    <label className="todoField">
                        <span>Повтор</span>
                        <select
                            value={repeat}
                            disabled={!task.dueAt}
                            onChange={(event) => patch({ repeat: event.target.value })}
                        >
                            {REPEAT_OPTIONS.map((item) => (
                                <option key={item.value} value={item.value}>{item.label}</option>
                            ))}
                        </select>
                    </label>
                </div>

                {!task.dueAt ? (
                    <div className="todoPanel__hint">Напоминание и повтор включаются, когда у задачи есть срок.</div>
                ) : null}

                <label className="todoField">
                    <span>Описание</span>
                    <textarea
                        value={description}
                        rows={4}
                        maxLength={8000}
                        placeholder="Подробности, размеры, договорённости…"
                        onFocus={() => { editingRef.current.description = true; }}
                        onChange={(event) => setDescription(event.target.value)}
                        onBlur={saveDescription}
                    />
                </label>

                <section className="todoPanel__section">
                    <h3>
                        Подзадачи
                        {subtasks.length ? <small>{doneSubtasks} из {subtasks.length}</small> : null}
                    </h3>

                    {subtasks.map((item) => (
                        <div key={item._id} className={`todoSub ${item.status === "done" ? "is-done" : ""}`}>
                            <input
                                type="checkbox"
                                checked={item.status === "done"}
                                onChange={(event) => run(
                                    () => actions.updateTask(item._id, { status: event.target.checked ? "done" : "todo" }),
                                    "Не удалось сохранить"
                                )}
                                aria-label={item.title}
                            />
                            <span>{item.title}</span>
                            <button
                                type="button"
                                title="Удалить подзадачу"
                                onClick={() => run(() => actions.deleteTask(item), "Не удалось удалить подзадачу")}
                            >
                                ✕
                            </button>
                        </div>
                    ))}

                    <form
                        className="todoPanel__add"
                        onSubmit={(event) => {
                            event.preventDefault();
                            addSubtask();
                        }}
                    >
                        <input
                            type="text"
                            value={subtaskTitle}
                            maxLength={300}
                            placeholder="Новая подзадача"
                            onChange={(event) => setSubtaskTitle(event.target.value)}
                        />
                        <button type="submit" disabled={!subtaskTitle.trim()}>Добавить</button>
                    </form>
                </section>

                <section
                    className={`todoPanel__section todoPanel__files ${dragging ? "is-dragging" : ""}`}
                    onDragOver={(event) => {
                        if (!Array.from(event.dataTransfer?.types || []).includes("Files")) return;
                        event.preventDefault();
                        setDragging(true);
                    }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={(event) => {
                        if (!event.dataTransfer?.files?.length) return;
                        event.preventDefault();
                        setDragging(false);
                        uploadFiles(event.dataTransfer.files);
                    }}
                >
                    <h3>
                        Файлы и голосовые
                        {attachments.length ? <small>{attachments.length}</small> : null}
                    </h3>

                    {attachments.map((item) => (
                        <TodoAttachment
                            key={item._id}
                            taskId={task._id}
                            attachment={item}
                            meId={meId}
                            commentCount={commentCounts[item._id] || 0}
                            commenting={commentFileId === item._id}
                            canDelete={isOwner
                                || getId(item.uploadedBy) === String(meId)
                                || getId(task.createdBy) === String(meId)}
                            onComment={startFileComment}
                            onDelete={(attachment) => actions.deleteAttachment(task._id, attachment._id)}
                            onRetryTranscript={(attachment) => actions.retryTranscript(task._id, attachment._id)}
                        />
                    ))}

                    {uploads.map((item) => (
                        <div key={item.key} className="todoUpload">
                            <span>{item.name}</span>
                            <i><b style={{ width: `${Math.max(4, item.percent)}%` }} /></i>
                            <small>{item.percent}%</small>
                        </div>
                    ))}

                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        hidden
                        onChange={(event) => {
                            uploadFiles(event.target.files);
                            event.target.value = "";
                        }}
                    />
                    <button
                        type="button"
                        className="todoPanel__attach"
                        onClick={() => fileInputRef.current?.click()}
                    >
                        + Добавить файл
                        <small>или перетащите его сюда</small>
                    </button>
                </section>

                <section className="todoPanel__section">
                    <h3>
                        Комментарии
                        {comments.length ? <small>{comments.length}</small> : null}
                    </h3>

                    {comments.map((item) => {
                        const file = item.attachmentId ? fileById[item.attachmentId] : null;
                        const canDelete = isOwner || getId(item.author) === String(meId);

                        return (
                            <div key={item._id} className="todoComment">
                                <div className="todoComment__head">
                                    <b>{userName(item.author, meId)}</b>
                                    <small>{whenText(item.createdAt)}</small>
                                    {canDelete ? (
                                        <button
                                            type="button"
                                            title="Удалить комментарий"
                                            onClick={() => run(
                                                () => actions.deleteComment(task._id, item._id),
                                                "Не удалось удалить комментарий"
                                            )}
                                        >
                                            ✕
                                        </button>
                                    ) : null}
                                </div>
                                {item.attachmentId ? <TodoFileRef taskId={task._id} attachment={file || null} /> : null}
                                <p>{item.text}</p>
                            </div>
                        );
                    })}

                    {commentFile ? (
                        <div className="todoPanel__commentTarget">
                            Комментарий к файлу: <b>{commentFile.originalName || "вложение"}</b>
                            <button type="button" onClick={() => setCommentFileId("")} title="Писать ко всей задаче">✕</button>
                        </div>
                    ) : null}

                    <form
                        className="todoPanel__comment"
                        onSubmit={(event) => {
                            event.preventDefault();
                            sendComment();
                        }}
                    >
                        <textarea
                            ref={commentRef}
                            value={comment}
                            rows={2}
                            maxLength={4000}
                            placeholder={commentFile ? "Что именно на этом файле…" : "Комментарий (Enter — отправить)"}
                            onChange={(event) => setComment(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === "Enter" && !event.shiftKey) {
                                    event.preventDefault();
                                    sendComment();
                                }
                            }}
                        />
                        <button type="submit" disabled={!comment.trim()}>Отправить</button>
                    </form>
                </section>

                <div className="todoPanel__facts">
                    {task.createdBy ? <span>Создал: {userName(task.createdBy, meId)} · {whenText(task.createdAt)}</span> : null}
                    {task.startedAt ? <span>Взял в работу: {userName(task.startedBy, meId)} · {whenText(task.startedAt)}</span> : null}
                    {task.completedAt ? <span>Готово: {userName(task.completedBy, meId)} · {whenText(task.completedAt)}</span> : null}
                    {task.shared ? <span>Отправлена в чат карточкой</span> : null}
                </div>
            </div>

            {error ? <div className="todoPanel__error" role="alert">{error}</div> : null}
            {notice ? <div className="todoPanel__notice" role="status">{notice}</div> : null}

            <div className="todoPanel__foot">
                <button type="button" onClick={() => onShare(task)}>Отправить в чат</button>
                <button type="button" onClick={copyAsText}>Скопировать текстом</button>
                {canDeleteTask ? (
                    <button type="button" className="is-danger" onClick={removeTask}>Удалить</button>
                ) : null}
            </div>
        </aside>
    );
}
